import { useState, useEffect } from "react";
import { MapPin, Plus, PencilSimple, Star, Trash, X, CheckCircle, SpinnerGap } from "@phosphor-icons/react";
import { myApi } from "../../../api";
import { validateAddressForm } from "@/shared/checkout/addressValidation";

const INPUT = "w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1829]/30 focus:border-[#0F1829] transition-all";
const LABEL = "text-[11px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mb-1 block";

const EMPTY_ADDRESS = {
  Nome: "", Nif: "", Morada: "", Localidade: "", CodigoPostal: "",
  ID_Pais: "PRT", Principal: "N",
};

/**
 * Reusable address picker used for BOTH shipping and billing in the checkout.
 * - `kind` is either "shipping" or "billing" (drives API endpoints + testids)
 * - `selectedId` + `onSelect` — controlled selection bubbled up to the page
 * - addresses are fetched on mount and after every mutation
 */
export default function AddressSection({ kind, title, selectedId, onSelect, countries = [], invalid = "" }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("list"); // list | new | edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ADDRESS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState("");

  const isBilling = kind === "billing";
  // ID_TipoMorada é CHAR na BD: 'F'=Facturação, 'R'=Residência, 'P'=Patronal.
  // Antes: '1'/'2' (convenção legacy que rebentava foreign key na BD).
  // Bug fix 2026-07-29: cliente 120966 (Elisabete Nunes) reportou "Tipo de
  // morada inválido ('2')" — resolvido a usar os códigos correctos.
  const tipoMorada = isBilling ? "F" : "R";
  const testidPrefix = `${kind}-address`;

  const reload = async () => {
    try {
      setError("");
      const r = isBilling ? await myApi.shopBillingAddresses() : await myApi.shopDeliveryAddresses();
      const items = r?.items || [];
      setAddresses(items);
      // Auto-select default (Principal=S) if nothing picked yet OR if the
      // previously stored selection points to an address that no longer exists
      // (e.g. stale sessionStorage carried over from a previous session).
      const stillExists = selectedId && items.some((a) => String(a.ID_Cliente_Morada) === String(selectedId));
      if ((!selectedId || !stillExists) && items.length > 0) {
        const def = items.find((a) => a.Principal === "S") || items[0];
        onSelect?.(String(def.ID_Cliente_Morada));
      }
    } catch (e) {
      setError(e.message || "Erro ao carregar moradas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Erro visível: mensagem + campo realçado a vermelho + scroll/foco no campo
  // em falta (mesmo padrão do registo e das moradas do perfil).
  const falhar = (msg, campo) => {
    setError(msg); setErrorField(campo || "");
    setTimeout(() => {
      const el = campo ? document.getElementById(`${testidPrefix}-f-${campo}`) : null;
      const target = el || document.querySelector(`[data-testid="${testidPrefix}-error"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (el) el.focus({ preventScroll: true });
    }, 60);
  };
  const fcls = (campo) => INPUT + (errorField === campo ? " !border-[#DC2626] !border-2 !bg-red-50" : "");

  const startNew = () => { setForm(EMPTY_ADDRESS); setMode("new"); setError(""); setErrorField(""); };
  const startEdit = (addr) => {
    setEditing(addr);
    setForm({
      Nome: addr.Nome || "", Nif: addr.Nif || "",
      Morada: addr.Morada || "", Localidade: addr.Localidade || "",
      CodigoPostal: addr.CodigoPostal || "", ID_Pais: addr.ID_Pais || "PRT",
      Principal: addr.Principal || "N",
    });
    setMode("edit");
    setError(""); setErrorField("");
  };
  const cancel = () => { setMode("list"); setEditing(null); setError(""); setErrorField(""); };

  const submit = async (e) => {
    e.preventDefault();
    const v = validateAddressForm(form);
    if (!v.ok) { falhar(v.message, v.field); return; }
    setBusy(true); setError(""); setErrorField("");
    try {
      const payload = { ...form, ID_Tipo_Morada: tipoMorada };
      const r = mode === "new"
        ? await myApi.shopAddressAdd(payload)
        : await myApi.shopAddressUpdate({ ...payload, ID_Cliente_Morada: editing.ID_Cliente_Morada });
      if (r?.success === false) { falhar(r.message || "Erro ao guardar."); return; }
      cancel();
      await reload();
    } catch (e) {
      falhar(e.message || "Erro ao guardar.");
    } finally { setBusy(false); }
  };

  const remove = async (addr) => {
    if (!window.confirm("Remover esta morada?")) return;
    setBusy(true);
    try {
      const r = await myApi.shopAddressRemove(addr.ID_Cliente_Morada);
      if (r?.success === false) setError(r.message || "Não foi possível remover.");
      if (String(selectedId) === String(addr.ID_Cliente_Morada)) onSelect?.("");
      await reload();
    } finally { setBusy(false); }
  };

  const setDefault = async (addr) => {
    setBusy(true);
    try {
      if (isBilling) await myApi.shopAddressSetDefaultBilling(addr.ID_Cliente_Morada);
      else await myApi.shopAddressSetDefaultShipping(addr.ID_Cliente_Morada);
      await reload();
    } finally { setBusy(false); }
  };

  return (
    <section
      className={"bg-white rounded-xl p-5 transition-colors " + (invalid
        ? "border-2 border-[#DC2626] ring-2 ring-red-100 bg-red-50/40"
        : "border border-[#E5E7EB]")}
      data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-black tracking-tighter text-[#0F1829] flex items-center gap-2" style={{ fontFamily: "'Fraunces', sans-serif" }}>
          <MapPin size={18} className={invalid ? "text-[#DC2626]" : "text-[#002480]"} />
          {title}
        </h2>
        {mode === "list" && (
          <button type="button" onClick={startNew} data-testid={`${testidPrefix}-new-btn`}
            className="text-xs font-medium text-[#002480] hover:text-[#0F1829] flex items-center gap-1">
            <Plus size={14} /> Nova morada
          </button>
        )}
      </div>

      {invalid && (
        <p className="mb-3 text-xs font-bold text-[#DC2626] flex items-center gap-1.5" data-testid={`${testidPrefix}-invalid`}>
          <span className="w-4 h-4 rounded-full bg-[#DC2626] text-white text-[10px] grid place-items-center shrink-0">!</span>
          {invalid}
        </p>
      )}

      {error && (
        <div className="mb-3 p-2.5 rounded-md bg-red-50 border-2 border-[#DC2626] text-[#DC2626] text-xs font-bold flex items-start gap-2" role="alert" data-testid={`${testidPrefix}-error`}>
          <span className="w-4 h-4 rounded-full bg-[#DC2626] text-white text-[10px] grid place-items-center shrink-0">!</span>
          <span>{error}</span>
        </div>
      )}

      {mode !== "list" ? (
        <form onSubmit={submit} className="space-y-3" data-testid={`${testidPrefix}-form`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={LABEL}>Nome</label>
              <input id={`${testidPrefix}-f-Nome`} className={fcls("Nome")} value={form.Nome} onChange={(e) => setForm({ ...form, Nome: e.target.value })}
                data-testid={`${testidPrefix}-form-nome`} />
            </div>
            <div>
              <label className={LABEL}>NIF</label>
              <input id={`${testidPrefix}-f-Nif`} className={fcls("Nif")} value={form.Nif} onChange={(e) => setForm({ ...form, Nif: e.target.value })}
                data-testid={`${testidPrefix}-form-nif`} />
            </div>
          </div>
          <div>
            <label className={LABEL}>Morada</label>
            <input id={`${testidPrefix}-f-Morada`} className={fcls("Morada")} value={form.Morada} onChange={(e) => setForm({ ...form, Morada: e.target.value })}
              data-testid={`${testidPrefix}-form-morada`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={LABEL}>Localidade</label>
              <input id={`${testidPrefix}-f-Localidade`} className={fcls("Localidade")} value={form.Localidade} onChange={(e) => setForm({ ...form, Localidade: e.target.value })}
                data-testid={`${testidPrefix}-form-localidade`} />
            </div>
            <div>
              <label className={LABEL}>Código Postal</label>
              <input id={`${testidPrefix}-f-CodigoPostal`} className={fcls("CodigoPostal")} value={form.CodigoPostal} onChange={(e) => setForm({ ...form, CodigoPostal: e.target.value })}
                data-testid={`${testidPrefix}-form-codpostal`} />
            </div>
            <div>
              <label className={LABEL}>País</label>
              <select id={`${testidPrefix}-f-ID_Pais`} className={fcls("ID_Pais")} value={form.ID_Pais} onChange={(e) => setForm({ ...form, ID_Pais: e.target.value })}
                data-testid={`${testidPrefix}-form-pais`}>
                {countries.length === 0 && <option value="PRT">Portugal</option>}
                {countries.map((c) => (
                  <option key={c.ID_Pais} value={c.ID_Pais}>{c.Descricao || c.Nome || c.ID_Pais}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#6B7280]">
            <input type="checkbox" checked={form.Principal === "S"}
              onChange={(e) => setForm({ ...form, Principal: e.target.checked ? "S" : "N" })}
              data-testid={`${testidPrefix}-form-principal`} />
            Definir como morada principal
          </label>

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={busy} data-testid={`${testidPrefix}-form-submit`}
              className="px-4 py-2 bg-[#0F1829] text-white text-sm font-semibold rounded-md hover:bg-[#002480] disabled:opacity-50 transition-colors flex items-center gap-2">
              {busy && <SpinnerGap size={14} className="animate-spin" />}
              {mode === "new" ? "Adicionar morada" : "Guardar alterações"}
            </button>
            <button type="button" onClick={cancel} disabled={busy}
              className="px-4 py-2 text-[#6B7280] text-sm hover:bg-[#F3F4F6] rounded-md transition-colors"
              data-testid={`${testidPrefix}-form-cancel`}>
              Cancelar
            </button>
          </div>
        </form>
      ) : loading ? (
        <div className="py-6 text-center text-sm text-[#6B7280]">A carregar…</div>
      ) : addresses.length === 0 ? (
        <div className="py-6 text-center text-sm text-[#6B7280]">Sem moradas guardadas. Adicione uma para continuar.</div>
      ) : (
        <div className="space-y-2">
          {addresses.map((a) => {
            const id = String(a.ID_Cliente_Morada);
            const picked = String(selectedId) === id;
            return (
              <div key={id}
                data-testid={`${testidPrefix}-row-${id}`}
                onClick={() => onSelect?.(id)}
                className={"p-3 rounded-lg border cursor-pointer transition-colors flex items-start gap-3 "
                  + (picked ? "border-[#0F1829] bg-[#F5F5F2] ring-1 ring-[#0F1829]" : "border-[#E5E7EB] hover:bg-[#F9FAFB]")}>
                <div className={"w-4 h-4 rounded-full shrink-0 mt-1 border-2 " + (picked ? "border-[#0F1829] bg-[#0F1829]" : "border-[#D1D5DB]")}>
                  {picked && <CheckCircle size={14} weight="fill" className="text-white -mt-[3px] -ml-[3px]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0F1829] flex items-center gap-2">
                    {a.Nome || "Morada"}
                    {a.Principal === "S" && (
                      <span className="text-[10px] bg-[#FEF3C7] text-[#92400E] px-1.5 py-0.5 rounded font-bold uppercase flex items-center gap-0.5">
                        <Star size={10} weight="fill" /> Principal
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-[#6B7280] mt-0.5 truncate">{a.Morada}</p>
                  <p className="text-xs text-[#6B7280] truncate">{a.CodigoPostal} {a.Localidade} · {a.ID_Pais}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {a.Principal !== "S" && (
                    <button type="button" disabled={busy}
                      onClick={(e) => { e.stopPropagation(); setDefault(a); }}
                      data-testid={`${testidPrefix}-default-${id}`}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#6B7280] hover:text-[#0F1829] hover:bg-white transition-colors"
                      title="Definir como principal">
                      <Star size={14} />
                    </button>
                  )}
                  <button type="button" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); startEdit(a); }}
                    data-testid={`${testidPrefix}-edit-${id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#6B7280] hover:text-[#0F1829] hover:bg-white transition-colors"
                    title="Editar">
                    <PencilSimple size={14} />
                  </button>
                  <button type="button" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); remove(a); }}
                    data-testid={`${testidPrefix}-remove-${id}`}
                    className="w-7 h-7 flex items-center justify-center rounded-md text-[#6B7280] hover:text-[#FF2B2B] hover:bg-red-50 transition-colors"
                    title="Remover">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
